import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import client from "@/Graphql/apolloClient";
import { useQuery } from "@apollo/client";
import { EXPLORE_PROPERTY_BUTTON, GET_MARKETPLACE_COUNTRIES } from "@/Graphql/Queries";
import { GET_MY_CART_ITEMS } from "@/Graphql/cartQueries";
import { Card, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, MapPin, Sparkles, Zap } from "lucide-react";
import { generateMockData } from "@/Graphql/MockData";
import { useAuth } from "@/utils/authContext";
import moment from "moment";
import { PACKAGE_BILLBOARD } from "@/Graphql/packageQurie";
import logger from '@/utils/logger';
import { fetchContentJson, extractPanelData, getFieldValue } from "@/utils/contentJsonParser";


interface FeaturedBillboardsProps {
  currencyCode: string;
  countryId: string;
  base_path: string;
  isExploreEnabled: boolean;
  isLoginEnabled: boolean;
  exposure: string;
}

export default function FeaturedBillboards({ currencyCode, countryId, base_path, isExploreEnabled, isLoginEnabled, exposure }: FeaturedBillboardsProps) {
  const navigate = useNavigate();
  // TODO: Replace with actual authentication logic
  const { isLoggedIn } = useAuth();
  const [exploreProperties, setExploreProperties] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [sectionTitle, setSectionTitle] = useState<string>("");
  const [sectionPrimaryHeader, setSectionPrimaryHeader] = useState<string>("");
  const [sectionSecondaryHeader, setSectionSecondaryHeader] = useState<string>("");
  const [sectionDescription, setSectionDescription] = useState<string>("");
  const [highImpactPackages, setHighImpactPackages] = useState<any[]>([]);
  const [cartCountryId, setCartCountryId] = useState<string>("");
  const [countriesMap, setCountriesMap] = useState<{ [key: string]: string }>({}); // Map country names to IDs

  const startDate = moment().format("YYYY-MM-DD");
  const endDate = moment().endOf('month').format("YYYY-MM-DD");

  const sessionDefaultCountryId = localStorage.getItem('sessionDefaultCountryId');

  // Fetch marketplace countries and build countries map
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const { data } = await client.query({
          query: GET_MARKETPLACE_COUNTRIES,
          variables: { exposure: "MARKETPLACE" },
          fetchPolicy: 'cache-first'
        });

        logger.log('✅ Marketplace Countries Data:', data);

        if (data?.getMarketplaceCountries && data.getMarketplaceCountries.length > 0) {
          // Create a map of country names to IDs
          const countryMap: { [key: string]: string } = {};
          data.getMarketplaceCountries.forEach((country: any) => {
            countryMap[country.name] = country.id;
          });

          setCountriesMap(countryMap);
          logger.log('✅ Countries map created:', countryMap);
        }
      } catch (error) {
        logger.error('❌ Error fetching marketplace countries:', error);
        // Try to get from localStorage cache if available
        const cachedMap = localStorage.getItem('countriesNameToIdMap');
        if (cachedMap) {
          setCountriesMap(JSON.parse(cachedMap));
          logger.log('✅ Using cached countries map');
        }
      }
    };

    fetchCountries();
  }, []);

  // Determine which countryId to use: cartCountryId if cart has items, otherwise sessionDefaultCountryId
  const selectedCountryID = localStorage.getItem('selectedCountryId');
  const effectiveCountryId = !isLoggedIn ? selectedCountryID : cartCountryId || sessionDefaultCountryId;

  const { data: packageData, refetch: refetchPackages } = useQuery(PACKAGE_BILLBOARD, {
    fetchPolicy: 'network-only',
    variables: {
      countryId: effectiveCountryId,
      "packageDate": startDate,
      startDate,
      endDate,
      "packageType": "DAYWISE",
      "exposure": exposure,
      "publisherId": "",
      "sort": "purchaseFrequency,desc",
      "size": 10,
      "page": 0
    },
    skip: !effectiveCountryId 
  });

  // Fetch cart items data
  const fetchCartData = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const userId = localStorage.getItem('userId');

      if (!token || !userId) {
        logger.log('No auth token or userId found, skipping cart data fetch');
        return null;
      }

      const { data } = await client.query({
        query: GET_MY_CART_ITEMS,
        fetchPolicy: "network-only",
        variables: {
          accessToken: token,
          userId: userId,
        }
      });

      logger.log('Cart data from API in FeaturedPackages:', data.cartpageItem);
      return data.cartpageItem || [];
    } catch (error) {
      logger.error('Error fetching cart items in FeaturedPackages:', error);
      return [];
    }
  };

  useEffect(() => {
    if (packageData && packageData.packageBillboard) {
      setHighImpactPackages(packageData.packageBillboard);
    }
  }, [packageData]);

  // Fetch cart data when component mounts or when user is authenticated
  useEffect(() => {
    const loadCartData = async () => {
      try {
        const cartItems = await fetchCartData();
        logger.log('Cart data retrieved successfully:', cartItems);
        
        // Process cart response here
        if (cartItems && cartItems.length > 0) {
          logger.log('User has items in cart:', cartItems);
          
          // Extract country ID from first cart item - similar to login.tsx
          const firstCartItem = cartItems[0];
          logger.log('First cart item:', firstCartItem);
          
          let cartItemCountryId = '';
          
          // First, check if cartItemCountryId is already set (from login.tsx)
          if (firstCartItem?.cartItemCountryId) {
            cartItemCountryId = firstCartItem.cartItemCountryId;
            logger.log('✅ Country ID already set from login:', cartItemCountryId);
          }
          
          // If not, try to extract country name and map it using countriesMap
          if (!cartItemCountryId && firstCartItem?.cartItemCountry) {
            const cartCountryName = firstCartItem.cartItemCountry;
            logger.log('Country name from cart item:', cartCountryName);
            
            // Look up country ID using the countriesMap
            if (countriesMap && Object.keys(countriesMap).length > 0) {
              const foundCountryId = countriesMap[cartCountryName];
              if (foundCountryId) {
                cartItemCountryId = foundCountryId;
                logger.log(`✅ Mapped country name "${cartCountryName}" to ID: ${foundCountryId}`);
              } else {
                logger.log(`❌ Country name "${cartCountryName}" not found in countries map`);
                logger.log('Available countries:', Object.keys(countriesMap));
              }
            } else {
              logger.log('❌ Countries map is empty, cannot map country name to ID');
            }
          }
          
          // Fallback to other fields if still not found
          if (!cartItemCountryId) {
            cartItemCountryId = firstCartItem?.country?.id || 
                               firstCartItem?.billboardCountry ||
                               firstCartItem?.countryId;
            logger.log('Country ID from fallback fields:', cartItemCountryId);
          }
          
          if (cartItemCountryId) {
            logger.log('✅ Setting cartCountryId from cart item:', cartItemCountryId);
            setCartCountryId(cartItemCountryId);
          } else {
            logger.log('❌ No country ID found in cart item');
            setCartCountryId(''); // Use sessionDefaultCountryId
          }
          
        } else {
          logger.log('Cart is empty, using sessionDefaultCountryId');
          setCartCountryId(''); // Reset to use sessionDefaultCountryId
        }
      } catch (error) {
        logger.error('Failed to load cart data:', error);
        setCartCountryId(''); // Reset on error to use sessionDefaultCountryId
      }
    };

    loadCartData();
  }, [countriesMap]); // Re-run when countriesMap is loaded

  // Set contentData from props when configData changes
  useEffect(() => {
    const fetchContent = async () => {
      try {
        const data = await fetchContentJson();
        
        // Extract the packages panel
        const packagesPanel= extractPanelData(data, "PackagesPanel");
        
        if (packagesPanel) {
          setSectionTitle(getFieldValue(packagesPanel, "packages_title", ""));
          setSectionPrimaryHeader(getFieldValue(packagesPanel, "packages_primary_header", ""));
          setSectionSecondaryHeader(getFieldValue(packagesPanel, "packages_secondary_header", ""));
          setSectionDescription(getFieldValue(packagesPanel, "packages_description", ""));
        }

      } catch (error) {
        logger.error("Error fetching content:", error);
      }
    };

    fetchContent();
  }, []);

  // Fetch explore properties
  useEffect(() => {
    const fetchMockData = async () => {
      try {
        const mockResponse = await generateMockData(EXPLORE_PROPERTY_BUTTON);
        let mockData = mockResponse.data?.landingpageExploreButton;

        if (mockData && mockData.length > 0) {
          mockData = mockData.filter((item: any) => (item.billboardType || '').toLowerCase() !== 'classic-transit');
          const filteredData = mockData.filter((item: any) => {
            const type = (item.billboardType || item.type || '').toLowerCase().trim();
            return type === 'classic' || type === 'digital';
          });
          setExploreProperties(filteredData);
        } else {
          throw new Error('Mock data was empty or invalid');
        }
      } catch (error) {
        logger.error("Error fetching mock explore properties:", error);
        setExploreProperties([]); // Use fallback data when no IDs and not mock mode
        setLoading(false);

      } finally {
        setLoading(false);
      }
    };

    if (import.meta.env.VITE_APP_MOCK_DATA === "true") {
      setLoading(true);
      fetchMockData(); // Always fetch mock data when enabled
    } else if (highImpactPackages.length > 0) {
      setExploreProperties(highImpactPackages);
    }
  }, [highImpactPackages]);


  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsPerView, setItemsPerView] = useState(3);

  useEffect(() => {
    const updateItemsPerView = () => {
      if (window.innerWidth < 768) {
        setItemsPerView(1);
      } else if (window.innerWidth < 1024) {
        setItemsPerView(2);
      } else {
        setItemsPerView(3);
      }
    };

    updateItemsPerView();
    window.addEventListener('resize', updateItemsPerView);
    return () => window.removeEventListener('resize', updateItemsPerView);
  }, []);

  useEffect(() => {
    if (exploreProperties.length > itemsPerView) {
      const timer = setInterval(() => {
        setCurrentIndex((prevIndex) => {
          const maxIndex = exploreProperties.length - itemsPerView;
          return prevIndex >= maxIndex ? 0 : prevIndex + 1;
        });
      }, 4000); // Faster transition - every 4 seconds
      return () => clearInterval(timer);
    } else {
      setCurrentIndex(0); // Always show first slide if not enough items
    }
  }, [itemsPerView, exploreProperties.length]);

  const nextSlide = () => {
    setCurrentIndex((prevIndex) =>
      (prevIndex + 1) % (exploreProperties.length - itemsPerView + 1)
    );
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? exploreProperties.length - itemsPerView : prevIndex - 1
    );
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Premium':
        return 'bg-primary-600 text-white';
      case 'Available':
        return 'bg-forest-custom text-white';
      case 'High Traffic':
        return 'bg-accent-amber text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };

  const handleViewDetails = (inventoryItem: any) => {
    logger.log("Viewing details for inventory item:", inventoryItem);

    const billboardType =
      inventoryItem.registryPackageType ||
      inventoryItem.inventoryType ||
      inventoryItem.type ||
      inventoryItem.billboardType ||
      'digital';

    // Use campaign dates or fallback to today and end of month
    const campaignStartDate = startDate || moment().format("YYYY-MM-DD");
    const campaignEndDate = endDate || moment().endOf('month').format("YYYY-MM-DD");

    const campaignName = inventoryItem.name || inventoryItem.displayName || '';
    navigate(`/package/${inventoryItem.id}?type=${encodeURIComponent(billboardType)}`, {
      state: {
        currentPrice: inventoryItem.totalPrice || inventoryItem.price || 0,
        startDate: campaignStartDate,
        endDate: campaignEndDate,
        campaignName,
        currencyCode: inventoryItem?.currency || currencyCode || inventoryItem?.price?.localCurrencies?.code || 'N/A'
      }
    });
  };


  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 sm:mb-12 lg:mb-16">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-primary-100 to-forest-100 border border-primary-200 text-primary-700 px-6 py-3 rounded-full text-sm shadow-sm mb-6">
            <Sparkles className="h-4 w-4 text-primary-600" />
            <span className="text-lg sm:text-xl leading-relaxed">{sectionTitle}</span>
            <Zap className="h-4 w-4 text-forest-custom" />
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold leading-tight FontStyles mb-4 sm:mb-6">
            <span className="text-gray-900 block">{sectionPrimaryHeader}</span>
            <span className="bg-gradient-to-r from-primary-600 to-forest-custom text-green-600 bg-clip-text block">{sectionSecondaryHeader}</span>
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            {sectionDescription}
          </p>
        </div>

        <div className="relative overflow-hidden">
          {exploreProperties.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                  <Sparkles className="h-8 w-8 text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-600 mb-2">No Packages Found</h3>
              </div>
            </div>
          ) : (
            <>
          {/* Auto-scrolling carousel */}
          <div
            className="flex transition-all duration-700 ease-in-out"
            style={{ transform: `translateX(-${currentIndex * (100 / itemsPerView)}%)` }}
          >
            {exploreProperties.map((billboard, _index) => (
              <div
                key={billboard.id}
                className="w-full md:w-1/2 xl:w-1/3 flex-shrink-0 px-3 lg:px-4"
              >
                <Card className="card-hover border-0 shadow-lg overflow-hidden flex flex-col h-full group transition-all duration-500 hover:shadow-2xl hover:scale-[1.02]">
                  <div className="relative aspect-video w-full overflow-hidden">
                    <img
                      src={
                        (billboard.thumbnail &&
                          billboard.thumbnail !== 'null' &&
                          billboard.thumbnail.trim() !== '')
                          ? billboard.thumbnail
                          : (billboard.thumbnailPath &&
                            billboard.thumbnailPath !== 'null' &&
                            billboard.thumbnailPath.trim() !== '')
                            ? billboard.thumbnailPath
                            : `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`
                      }
                      alt={billboard.name || billboard.displayName || billboard.title}
                      className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-110"
                    />
                    {/* Animated overlay on hover */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    <Badge
                      className={`absolute top-3 right-3 text-xs font-medium px-2 py-1 ${getCategoryColor(billboard.category || billboard.billboardType)} transition-transform duration-300 group-hover:scale-110`}
                    >
                      {billboard.billboardType || billboard.category || 'PACKAGE'}
                    </Badge>
                    {/* Animated view indicator */}
                    {/* <div className="absolute bottom-8 left-3 bg-white/95 backdrop-blur-sm rounded-lg px-3 py-1 transform translate-y-full group-hover:translate-y-0 transition-transform duration-500">
                      <div className="flex items-center gap-1 text-sm font-semibold text-gray-900">
                        <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                        High Impact Location
                      </div>
                    </div> */}
                  </div>
                  <div className="flex flex-col flex-grow p-4 sm:p-6">
                    <div className="mb-3">
                      <CardTitle
                        className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 line-clamp-1 group-hover:text-primary-600 transition-colors duration-300"
                        title={(billboard.name || billboard.displayName || billboard.title)?.length > 30 ? (billboard.name || billboard.displayName || billboard.title) : ""}
                      >
                        {(billboard.name || billboard.displayName || billboard.title)?.length > 30
                          ? `${(billboard.name || billboard.displayName || billboard.title).slice(0, 30)}...`
                          : (billboard.name || billboard.displayName || billboard.title)}
                      </CardTitle>
                      <div className="flex items-center text-gray-600 group-hover:text-gray-800 transition-colors duration-300">
                        <MapPin className="h-4 w-4 mr-2 flex-shrink-0 text-primary-600" />
                        <span className="text-sm sm:text-base truncate">{billboard.country?.name || billboard.state?.name || billboard.location}</span>
                      </div>
                    </div>
                    {/* {(billboard.publisher?.name || billboard.mediaOwnerName) && (() => {
                      const ownerName = billboard.publisher?.name || billboard.mediaOwnerName;
                      const mediaOwner = getMediaOwner(ownerName);
                      if (mediaOwner) {
                        return (
                          <div className="flex items-center space-x-2">
                            <img
                              src={mediaOwner.logo}
                              alt={mediaOwner.name}
                              className="w-5 h-5 rounded-full object-cover flex-shrink-0"
                            />
                            <span className="font-medium text-emerald-600 hover:text-emerald-700 cursor-pointer">{mediaOwner.name}</span>
                          </div>
                        );
                      }
                      return <span className="font-medium"> by {ownerName}</span>;
                    })() || <span className="font-medium"> by N/A</span>} */}
                    <div className="mt-auto">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center text-forest-custom font-bold text-lg sm:text-xl group-hover:scale-105 transition-transform duration-300">
                          <span className="whitespace-nowrap">
                            {billboard.totalPrice
                              ? `${billboard.currency || currencyCode} ${billboard?.totalPrice?.toLocaleString()}`
                              : (billboard.billboardType === 'PACKAGE' && billboard?.price && typeof billboard?.price === 'number'
                                ? `${billboard.currency || currencyCode} ${billboard.price?.toLocaleString()}`
                                : (billboard.sellingRate?.monthly?.month1 != null && billboard.sellingRate?.monthly?.month1 !== ''
                                  ? `${currencyCode} ${billboard.sellingRate?.monthly?.month1?.toLocaleString()}`
                                  : `${currencyCode} N/A`))}
                          </span>
                          <span className="text-sm font-normal text-gray-600 ml-1">
                            /Package
                          </span>
                        </div>
                      </div>
                      <Button
                        className="w-full btn-primary-gradient hover:bg-primary-700 text-white font-medium py-2.5 transform hover:scale-[1.02] transition-all duration-200"
                        data-testid={`button-view-featured-${billboard.id}`}
                        onClick={() => handleViewDetails(billboard)}
                        style={
                          (!isLoggedIn && !isExploreEnabled && isLoginEnabled) || (isLoggedIn && !isLoginEnabled) ? { display: 'none' } : {}
                        }
                      >
                        View Details
                      </Button>
                    </div>
                  </div>
                </Card>
              </div>
            ))}
          </div>

        {/* Enhanced progress indicators */}
        {exploreProperties.length > itemsPerView && (
          <div className="flex justify-center mt-8 space-x-3">
            {Array.from({ length: exploreProperties.length - itemsPerView + 1 }).map((_, index) => (
              <button
                key={index}
                className={`relative overflow-hidden rounded-full transition-all duration-500 ${index === currentIndex
                  ? 'w-8 h-3 bg-primary-600 shadow-lg shadow-primary-600/40'
                  : 'w-3 h-3 bg-gray-300 hover:bg-gray-400'
                  }`}
                onClick={() => setCurrentIndex(index)}
                data-testid={`indicator-${index}`}
              >
                {index === currentIndex && (
                  <div className="absolute inset-0 bg-gradient-to-r from-primary-500 to-emerald-500 animate-pulse"></div>
                )}
              </button>
            ))}
          </div>
        )}

        {/* Navigation Buttons - Visible and accessible */}
      {exploreProperties.length > itemsPerView && (
        <>
          <Button
            variant="outline"
            size="icon"
            onClick={prevSlide}
            className="absolute left-2 sm:left-4 top-1/2 transform -translate-y-1/2 bg-white shadow-xl border-2 border-primary-600 text-primary-600 hover:bg-primary-600 hover:text-white w-10 h-10 sm:w-12 sm:h-12 z-30 rounded-full transition-all duration-300 hover:scale-110"
            data-testid="button-carousel-prev"
          >
            <ChevronLeft className="h-5 w-5 sm:h-6 sm:w-6" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={nextSlide}
            className="absolute right-2 sm:right-4 top-1/2 transform -translate-y-1/2 bg-white shadow-xl border-2 border-primary-600 text-primary-600 hover:bg-primary-600 hover:text-white w-10 h-10 sm:w-12 sm:h-12 z-30 rounded-full transition-all duration-300 hover:scale-110"
            data-testid="button-carousel-next"
          >
            <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6" />
          </Button>
        </>
      )}
    </>
  )}
        </div>
      </div>
    </section>
  );
}
